import * as React from 'react';

import { TextRange } from '@gitbook/slate';
import type { InlineProps } from '@gitbook/slate-react';

/**
 * Check if an inline is active, meaning it has been hovered or the selection
 * is inside of it.
 * This hook is used for performance purpose, it allows to load a light component
 * and to only load the full one when active.
 */
export function useIsInlineActive(props: UseIsInlineActiveBySelectionProps): [
    boolean,
    {
        onMouseOver: () => void;
    },
] {
    const [isActive, setIsActive] = React.useState(false);
    const isActiveBySelection = useIsInlineActiveBySelection(props);
    const activate = React.useCallback(() => {
        setIsActive(true);
    }, []);

    React.useEffect(() => {
        if (isActiveBySelection) {
            activate();
        }
    }, [isActiveBySelection, activate]);

    return [isActive || isActiveBySelection, { onMouseOver: activate }];
}

type UseIsInlineActiveBySelectionProps = Pick<InlineProps, 'editor' | 'node'>;

/**
 * Check if the inline is considered active.
 * The selection must be inside the node or surrounding it.
 */
export function useIsInlineActiveBySelection(props: UseIsInlineActiveBySelectionProps) {
    const { node, editor } = props;
    const { selection } = editor.value;

    if (!TextRange.isTextRange(selection)) {
        return false;
    }

    const prevText = editor.value.document.getPreviousTextBeforeNode(node.key);
    const hasEdgeInPrevText = prevText ? selection.hasEdgeAtEndOf(prevText) : false;
    const nextText = editor.value.document.getNextTextAfterNode(node.key);
    const hasEdgeInNextText = nextText ? selection.hasEdgeAtStartOf(nextText) : false;

    const values = [
        selection.hasFocusIn(node),
        selection.hasAnchorIn(node),
        hasEdgeInPrevText,
        hasEdgeInNextText,
    ];

    return values.filter((x) => x).length === 2;
}
